import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoToyo from '../../assets/images/toyo-logo-negative.png';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTeamsOpen, setIsTeamsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const teamsMenuRef = useRef(null);

  const teamItems = [
    { path: 'pubg', label: 'PUBG'},
    { path: 'valorant', label: 'VALORANT'},
    { path: 'simRacing', label: 'SIM RACING'},
  ];

  // Separamos el item de tienda del resto del menú
  const shopItem = { path: '/tienda', label: 'TIENDA' };
  const menuItems = [
    { path: '/', label: 'TOYO' },
    { 
      path: '/equipos', 
      label: 'EQUIPOS',
      hasSubmenu: true,
    },
    { path: '/noticias', label: 'NOTICIAS' },
    { path: '/contacto', label: 'CONTACTO' },
  ];

  useEffect(() => {
    function handleClickOutside(event) {
      if (teamsMenuRef.current && !teamsMenuRef.current.contains(event.target)) {
        setIsTeamsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsTeamsOpen(false);
    setIsMenuOpen(false);
  }, [location]);

  const handleTeamClick = (teamPath) => {
    navigate(`/equipos/${teamPath}`);
    setIsTeamsOpen(false);
    setIsMenuOpen(false);
  };

  const TeamsDropdown = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="absolute top-full left-1/2 transform -translate-x-1/2 bg-brand-dark/95 backdrop-blur-sm rounded-b-xl border border-brand-white/10 shadow-lg py-2 w-48"
    >
      {teamItems.map(team => (
        <button
          key={team.path}
          onClick={() => handleTeamClick(team.path)}
          className="w-full px-4 py-3 text-xl font-morganite tracking-wider text-brand-white/70 hover:text-brand-peach hover:bg-brand-red/20 transition-all duration-300 flex items-center"
        >
          {team.label}
        </button>
      ))}
    </motion.div>
  );

  return (
    <header className="fixed w-full z-50 bg-brand-red">
      <nav className="container mx-auto px-4 py-2">
        <div className="flex justify-between items-center">
          {/* Logo - Removed padding from container */}
          <Link to="/" className="flex items-center h-12 md:h-14">
            <img 
              src={logoToyo}
              alt="TOYO"
              className="h-full w-auto"
            />
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-12 flex-grow justify-center">
            {menuItems.map(item => (
              <div key={item.path} className="relative" ref={item.hasSubmenu ? teamsMenuRef : null}>
                {item.hasSubmenu ? (
                  <button
                    onClick={() => setIsTeamsOpen(!isTeamsOpen)}
                    className={`text-2xl font-morganite tracking-wider transition-all duration-300
                      ${location.pathname.includes('/equipos')
                        ? 'text-brand-red bg-brand-peach px-4 py-2 rounded' 
                        : 'text-brand-white hover:text-brand-red hover:bg-brand-peach px-4 py-2 rounded'
                      }`}
                  >
                    {item.label}
                    <span className="ml-1">{isTeamsOpen ? '▼' : '▶'}</span>
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`text-2xl font-morganite tracking-wider transition-all duration-300
                      ${location.pathname === item.path 
                        ? 'text-brand-red bg-brand-peach px-4 py-2 rounded' 
                        : 'text-brand-white hover:text-brand-red hover:bg-brand-peach px-4 py-2 rounded'
                      }`}
                  >
                    {item.label}
                  </Link>
                )}
                {item.hasSubmenu && (
                  <AnimatePresence>
                    {isTeamsOpen && <TeamsDropdown />}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* Shop Button - Desktop - Adjusted height and padding */}
          <div className="hidden md:block">
            <Link
              to={shopItem.path}
              className="text-2xl font-morganite tracking-wider transition-all duration-300 bg-brand-peach text-brand-dark h-12 inline-flex items-center justify-center px-8 rounded hover:bg-red-800 hover:text-brand-white"
            >
              {shopItem.label}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="md:hidden text-brand-white hover:text-brand-peach transition-colors"
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-4 pb-4 space-y-2"
            >
              {[...menuItems, shopItem].map(item => (
                item.hasSubmenu ? (
                  <div key={item.path}>
                    <button
                      onClick={() => setIsTeamsOpen(!isTeamsOpen)}
                      className={`w-full text-center py-3 text-xl font-morganite tracking-wider transition-all duration-300
                        ${location.pathname.includes('/equipos')
                          ? 'text-brand-red bg-brand-peach rounded' 
                          : 'text-brand-white hover:text-brand-red hover:bg-brand-peach rounded'
                        }`}
                    >
                      {item.label}
                      <span className="ml-1">{isTeamsOpen ? '▼' : '▶'}</span>
                    </button>
                    <AnimatePresence>
                      {isTeamsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="bg-brand-dark/95 backdrop-blur-sm mt-2 py-2 rounded-xl border border-brand-white/10"
                        >
                          {teamItems.map(team => (
                            <button
                              key={team.path}
                              onClick={() => handleTeamClick(team.path)}
                              className="w-full py-2 text-xl font-morganite tracking-wider text-brand-white/70 hover:text-brand-peach hover:bg-brand-red/20 transition-all duration-300 flex items-center justify-center"
                            >
                              {team.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block text-center py-3 text-xl font-morganite tracking-wider transition-all duration-300
                      ${location.pathname === item.path 
                        ? 'text-brand-red bg-brand-peach rounded' 
                        : item.path === '/tienda'
                          ? 'bg-brand-peach text-brand-red rounded'
                          : 'text-brand-white hover:text-brand-red hover:bg-brand-peach rounded'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

export default Header;